import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Slider } from './ui/slider';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { CalendarStrip } from './CalendarStrip';
import { 
    Droplets, Moon, Utensils, Dumbbell, Plus, Check, 
    Sparkles, Zap, ChevronRight, Star, Coffee, Clock,
    X, Loader2, Calendar, ChevronDown, HeartPulse
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NANOUK_HAPPY = 'https://images.unsplash.com/photo-1767381604151-bae00f2fb337?w=200&h=200&fit=crop';

export default function QuickActions({ language = 'de', onActionComplete }) {
    const { user } = useAuth();
    const [status, setStatus] = useState(null);
    const [favoriteMeals, setFavoriteMeals] = useState([]);
    const [favoriteWorkouts, setFavoriteWorkouts] = useState([]);
    const [loading, setLoading] = useState({});
    
    // Date selection for backdating
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [showDatePicker, setShowDatePicker] = useState(false);
    
    // Quick Log Drawers
    const [sleepDrawerOpen, setSleepDrawerOpen] = useState(false);
    const [mealDrawerOpen, setMealDrawerOpen] = useState(false);
    const [workoutDrawerOpen, setWorkoutDrawerOpen] = useState(false);
    
    // Sleep form
    const [sleepForm, setSleepForm] = useState({
        sleep_time: '23:00',
        wake_time: '07:00',
        quality: 7
    });
    
    // Quick meal form
    const [mealForm, setMealForm] = useState({
        name: '',
        calories: 300,
        meal_type: 'snack'
    });
    
    // Quick workout form
    const [workoutForm, setWorkoutForm] = useState({
        name: '',
        duration: 30,
        type: 'cardio'
    });
    
    const t = {
        de: {
            title: 'Schnellaktionen',
            subtitle: 'Ein Tap genügt!',
            water: 'Wasser',
            waterLogged: 'getrunken',
            sleep: 'Schlaf',
            sleepLogged: 'Schlaf erfasst',
            noSleep: 'Noch nicht erfasst',
            favorites: 'Favoriten',
            noFavorites: 'Noch keine Favoriten',
            quickWater: 'Schnell Wasser loggen',
            logged: 'Erfasst!',
            bottle: 'Flasche',
            quickSleep: 'Schlaf eintragen',
            quickMeal: 'Mahlzeit eintragen',
            quickWorkout: 'Workout eintragen',
            save: 'Speichern',
            cancel: 'Abbrechen',
            fellAsleep: 'Eingeschlafen',
            wokeUp: 'Aufgewacht',
            quality: 'Qualität',
            duration: 'Dauer (min)',
            mealName: 'Was hast du gegessen?',
            workoutName: 'Welches Workout?',
            today: 'Heute',
            backdating: 'Für vergangenes Datum eintragen'
        },
        en: {
            title: 'Quick Actions',
            subtitle: 'One tap is enough!',
            water: 'Water',
            waterLogged: 'logged',
            sleep: 'Sleep',
            sleepLogged: 'Sleep logged',
            noSleep: 'Not logged yet',
            favorites: 'Favorites',
            noFavorites: 'No favorites yet',
            quickWater: 'Quick log water',
            logged: 'Logged!',
            bottle: 'Bottle',
            quickSleep: 'Log Sleep',
            quickMeal: 'Log Meal',
            quickWorkout: 'Log Workout',
            save: 'Save',
            cancel: 'Cancel',
            fellAsleep: 'Fell asleep',
            wokeUp: 'Woke up',
            quality: 'Quality',
            duration: 'Duration (min)',
            mealName: 'What did you eat?',
            workoutName: 'Which workout?',
            today: 'Today',
            backdating: 'Log for past date'
        }
    }[language] || {};
    
    const isToday = selectedDate.toDateString() === new Date().toDateString();
    
    useEffect(() => {
        loadStatus();
        loadFavorites();
    }, [selectedDate]);
    
    const loadStatus = async () => {
        try {
            const dateStr = selectedDate.toISOString().split('T')[0];
            const response = await axios.get(`${API}/favorites/daily-status?date=${dateStr}`);
            setStatus(response.data);
        } catch (error) {
            console.error('Failed to load status:', error);
        }
    };
    
    const loadFavorites = async () => {
        try {
            const [mealsRes, workoutsRes] = await Promise.all([
                axios.get(`${API}/favorites/meals`),
                axios.get(`${API}/favorites/workouts`)
            ]);
            setFavoriteMeals(mealsRes.data.slice(0, 3));
            setFavoriteWorkouts(workoutsRes.data.slice(0, 2));
        } catch (error) {
            console.error('Failed to load favorites:', error);
        }
    };
    
    const handleQuickWater = async (amount) => {
        setLoading(prev => ({ ...prev, [`water_${amount}`]: true }));
        try {
            await axios.post(`${API}/nutrition/water`, {
                amount_ml: amount,
                logged_at: selectedDate.toISOString()
            });
            toast.success(`💧 ${amount}ml ${t.waterLogged}!`);
            loadStatus();
            onActionComplete?.();
        } catch (error) {
            toast.error('Fehler');
        } finally {
            setLoading(prev => ({ ...prev, [`water_${amount}`]: false }));
        }
    };
    
    const handleQuickSleep = async () => {
        setLoading(prev => ({ ...prev, sleep: true }));
        try {
            
            // Calculate sleep times
            const wakeDate = new Date(selectedDate);
            wakeDate.setHours(0, 0, 0, 0);
            
            const [sleepHour, sleepMin] = sleepForm.sleep_time.split(':').map(Number);
            const [wakeHour, wakeMin] = sleepForm.wake_time.split(':').map(Number);
            
            const sleepStart = new Date(wakeDate);
            sleepStart.setDate(sleepStart.getDate() - 1);
            sleepStart.setHours(sleepHour, sleepMin, 0, 0);
            
            if (sleepHour < 12) {
                sleepStart.setDate(sleepStart.getDate() + 1);
            }
            
            const sleepEnd = new Date(wakeDate);
            sleepEnd.setHours(wakeHour, wakeMin, 0, 0);
            
            await axios.post(`${API}/sleep`, {
                sleep_start: sleepStart.toISOString(),
                sleep_end: sleepEnd.toISOString(),
                quality_rating: sleepForm.quality
            });
            
            toast.success(`😴 ${t.sleepLogged}! +10 XP`);
            setSleepDrawerOpen(false);
            loadStatus();
            onActionComplete?.();
        } catch (error) {
            toast.error('Fehler');
        } finally {
            setLoading(prev => ({ ...prev, sleep: false }));
        }
    };
    
    const handleQuickMeal = async () => {
        if (!mealForm.name) return;
        setLoading(prev => ({ ...prev, meal: true }));
        try {
            await axios.post(`${API}/nutrition/food`, {
                name: mealForm.name,
                meal_type: mealForm.meal_type,
                calories: mealForm.calories,
                protein: 0,
                carbs: 0,
                fat: 0,
                logged_at: selectedDate.toISOString()
            });
            
            toast.success(`🍽️ ${mealForm.name} ${t.logged} +5 XP`);
            setMealDrawerOpen(false);
            setMealForm({ name: '', calories: 300, meal_type: 'snack' });
            loadStatus();
            onActionComplete?.();
        } catch (error) {
            toast.error('Fehler');
        } finally {
            setLoading(prev => ({ ...prev, meal: false }));
        }
    };
    
    const handleQuickWorkout = async () => {
        if (!workoutForm.name) return;
        setLoading(prev => ({ ...prev, workout: true }));
        try {
            await axios.post(`${API}/fitness/workouts`, {
                name: workoutForm.name,
                workout_type: workoutForm.type,
                duration_minutes: workoutForm.duration,
                calories_burned: Math.round(workoutForm.duration * 8),
                logged_at: selectedDate.toISOString()
            });
            
            toast.success(`💪 ${workoutForm.name} ${t.logged} +15 XP`);
            setWorkoutDrawerOpen(false);
            setWorkoutForm({ name: '', duration: 30, type: 'cardio' });
            loadStatus();
            onActionComplete?.();
        } catch (error) {
            toast.error('Fehler');
        } finally {
            setLoading(prev => ({ ...prev, workout: false }));
        }
    };
    
    const handleLogFavoriteMeal = async (mealId, mealName) => {
        setLoading(prev => ({ ...prev, [`meal_${mealId}`]: true }));
        try {
            await axios.post(`${API}/favorites/meals/${mealId}/log`, {
                logged_at: selectedDate.toISOString()
            });
            toast.success(`🍽️ ${mealName} ${t.logged} +5 XP`);
            loadStatus();
            onActionComplete?.();
        } catch (error) {
            toast.error('Fehler');
        } finally {
            setLoading(prev => ({ ...prev, [`meal_${mealId}`]: false }));
        }
    };
    
    const handleLogFavoriteWorkout = async (workoutId, workoutName) => {
        setLoading(prev => ({ ...prev, [`workout_${workoutId}`]: true }));
        try {
            await axios.post(`${API}/favorites/workouts/${workoutId}/log`, {
                logged_at: selectedDate.toISOString()
            });
            toast.success(`💪 ${workoutName} ${t.logged}`);
            loadStatus();
            onActionComplete?.();
        } catch (error) {
            toast.error('Fehler');
        } finally {
            setLoading(prev => ({ ...prev, [`workout_${workoutId}`]: false }));
        }
    };
    
    const waterProgress = status ? (status.logged.water_ml / status.logged.water_goal) * 100 : 0;
    
    const calculateSleepDuration = () => {
        const [sleepH, sleepM] = sleepForm.sleep_time.split(':').map(Number);
        const [wakeH, wakeM] = sleepForm.wake_time.split(':').map(Number);
        let hours = wakeH - sleepH;
        let mins = wakeM - sleepM;
        if (hours < 0) hours += 24;
        if (mins < 0) { mins += 60; hours--; }
        return (hours + mins / 60).toFixed(1);
    };
    
    return (
        <>
            <Card className="border">
                <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl overflow-hidden">
                                <img src={NANOUK_HAPPY} alt="Nanouk" className="w-full h-full object-cover" />
                            </div>
                            <div>
                                <CardTitle className="text-lg flex items-center gap-2">
                                    <Zap className="w-5 h-5 text-yellow-500" />
                                    {t.title}
                                </CardTitle>
                                <p className="text-sm text-muted-foreground">{t.subtitle}</p>
                            </div>
                        </div>
                    </div>
                </CardHeader>
                
                <CardContent className="space-y-4">
                    {/* Date Selection */}
                    <div className="space-y-2">
                        <button
                            onClick={() => setShowDatePicker(!showDatePicker)}
                            className="w-full flex items-center justify-between p-2 rounded-lg bg-card hover:bg-muted/50 transition-colors"
                        >
                            <span className="text-sm flex items-center gap-2">
                                <Calendar className="w-4 h-4 text-primary" />
                                {isToday ? (
                                    <span className="text-primary font-medium">{t.today}</span>
                                ) : (
                                    <span className="text-amber-400">
                                        📅 {selectedDate.toLocaleDateString(language === 'de' ? 'de-DE' : 'en-US', {
                                            weekday: 'short', day: 'numeric', month: 'short'
                                        })}
                                    </span>
                                )}
                            </span>
                            <ChevronDown className={`w-4 h-4 transition-transform ${showDatePicker ? 'rotate-180' : ''}`} />
                        </button>
                        
                        <AnimatePresence>
                            {showDatePicker && (
                                <motion.div
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: 'auto', opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                >
                                    <CalendarStrip
                                        selectedDate={selectedDate}
                                        onDateChange={(date) => {
                                            setSelectedDate(date);
                                            setShowDatePicker(false);
                                        }}
                                    />
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                    
                    {/* Quick Action Buttons Grid */}
                    <div className="grid grid-cols-3 gap-2">
                        <Button
                            variant="outline"
                            className="flex-col h-auto py-3 gap-1 hover:bg-primary/10 hover:border-primary"
                            onClick={() => setSleepDrawerOpen(true)}
                            data-testid="quick-sleep-btn"
                        >
                            <Moon className="w-5 h-5 icon-sleep" />
                            <span className="text-xs">{t.sleep}</span>
                            {status?.logged.sleep && <Check className="w-3 h-3 text-green-500" />}
                        </Button>
                        
                        <Button
                            variant="outline"
                            className="flex-col h-auto py-3 gap-1 hover:bg-orange-500/10 hover:border-orange-500"
                            onClick={() => setMealDrawerOpen(true)}
                            data-testid="quick-meal-btn"
                        >
                            <Utensils className="w-5 h-5 icon-fitness" />
                            <span className="text-xs">{status?.logged.meals || 0}x</span>
                        </Button>
                        
                        <Button
                            variant="outline"
                            className="flex-col h-auto py-3 gap-1 hover:bg-primary/10 hover:border-primary"
                            onClick={() => setWorkoutDrawerOpen(true)}
                            data-testid="quick-workout-btn"
                        >
                            <Dumbbell className="w-5 h-5 icon-health" />
                            <span className="text-xs">{status?.logged.workouts || 0}x</span>
                        </Button>
                    </div>
                    
                    {/* Quick Water Buttons */}
                    <div className="space-y-2">
                        <div className="flex items-center justify-between">
                            <span className="text-sm font-medium flex items-center gap-2">
                                <Droplets className="w-4 h-4 text-blue-500" />
                                {t.quickWater}
                            </span>
                            <span className="text-xs text-muted-foreground">
                                {status?.logged.water_ml || 0} / {status?.logged.water_goal || 2500} ml
                            </span>
                        </div>
                        <Progress value={Math.min(waterProgress, 100)} className="h-2" />
                        <div className="flex gap-2">
                            {[250, 500, 750].map((amount) => (
                                <Button
                                    key={amount}
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleQuickWater(amount)}
                                    disabled={loading[`water_${amount}`]}
                                    className="flex-1 gap-1 hover:bg-blue-500/10 hover:border-blue-500"
                                    data-testid={`quick-water-${amount}`}
                                >
                                    {loading[`water_${amount}`] ? (
                                        <Loader2 className="w-3 h-3 animate-spin" />
                                    ) : (
                                        <>
                                            <Droplets className="w-3 h-3" />
                                            {amount === 750 ? t.bottle : `${amount}ml`}
                                        </>
                                    )}
                                </Button>
                            ))}
                        </div>
                    </div>
                    
                    {/* Favorite Meals */}
                    {favoriteMeals.length > 0 && (
                        <div className="space-y-2">
                            <span className="text-sm font-medium flex items-center gap-2">
                                <Utensils className="w-4 h-4 text-orange-500" />
                                {t.favorites}
                            </span>
                            <div className="flex flex-wrap gap-2">
                                {favoriteMeals.map((meal) => (
                                    <Button
                                        key={meal.id}
                                        variant="outline"
                                        size="sm"
                                        onClick={() => handleLogFavoriteMeal(meal.id, meal.name)}
                                        disabled={loading[`meal_${meal.id}`]}
                                        className="gap-1 hover:bg-orange-500/10 hover:border-orange-500"
                                    >
                                        {loading[`meal_${meal.id}`] ? (
                                            <Loader2 className="w-3 h-3 animate-spin" />
                                        ) : (
                                            <>
                                                <Plus className="w-3 h-3" />
                                                {meal.name}
                                                <Badge variant="secondary" className="ml-1 text-xs">
                                                    {meal.calories}kcal
                                                </Badge>
                                            </>
                                        )}
                                    </Button>
                                ))}
                            </div>
                        </div>
                    )}
                    
                    {/* Favorite Workouts */}
                    {favoriteWorkouts.length > 0 && (
                        <div className="space-y-2">
                            <span className="text-sm font-medium flex items-center gap-2">
                                <Dumbbell className="w-4 h-4 icon-health" />
                                {language === 'de' ? 'Trainings' : 'Workouts'}
                            </span>
                            <div className="flex flex-wrap gap-2">
                                {favoriteWorkouts.map((workout) => (
                                    <Button
                                        key={workout.id}
                                        variant="outline"
                                        size="sm"
                                        onClick={() => handleLogFavoriteWorkout(workout.id, workout.name)}
                                        disabled={loading[`workout_${workout.id}`]}
                                        className="gap-1 hover:bg-primary/10 hover:border-primary"
                                    >
                                        {loading[`workout_${workout.id}`] ? (
                                            <Loader2 className="w-3 h-3 animate-spin" />
                                        ) : (
                                            <>
                                                <Plus className="w-3 h-3" />
                                                {workout.name}
                                                <Badge variant="secondary" className="ml-1 text-xs">
                                                    {workout.duration_minutes}min
                                                </Badge>
                                            </>
                                        )}
                                    </Button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Navigation shortcuts (merged from the former bottom "Schnellaktionen" card) */}
                    <div className="grid grid-cols-2 gap-2 pt-1" data-testid="quick-nav-shortcuts">
                        <a
                            href="/health"
                            className="flex items-center justify-center gap-2 p-2.5 rounded-xl bg-rose-50 dark:bg-rose-950/30 hover:bg-rose-100 dark:hover:bg-rose-950/50 transition-colors text-sm font-medium"
                            data-testid="quick-nav-symptom"
                        >
                            <HeartPulse className="w-4 h-4 text-rose-600 dark:text-rose-400" />
                            {language === 'de' ? 'Symptom erfassen' : 'Log symptom'}
                        </a>
                        <a
                            href="/coach"
                            className="flex items-center justify-center gap-2 p-2.5 rounded-xl bg-purple-50 dark:bg-purple-950/30 hover:bg-purple-100 dark:hover:bg-purple-950/50 transition-colors text-sm font-medium"
                            data-testid="quick-nav-coach"
                        >
                            <Zap className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                            {language === 'de' ? 'Coach fragen' : 'Ask coach'}
                        </a>
                    </div>
                </CardContent>
            </Card>
            
            {/* Sleep Drawer */}
            <AnimatePresence>
                {sleepDrawerOpen && (
                    <>
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
                            onClick={() => setSleepDrawerOpen(false)}
                        />
                        <motion.div
                            initial={{ y: '100%' }}
                            animate={{ y: 0 }}
                            exit={{ y: '100%' }}
                            transition={{ type: 'spring', damping: 25 }}
                            className="fixed bottom-0 left-0 right-0 z-50 bg-card rounded-t-3xl border-t border-border max-h-[85vh] overflow-y-auto"
                        >
                            <div className="flex justify-center py-2">
                                <div className="w-12 h-1.5 bg-muted-foreground/20 rounded-full" />
                            </div>
                            
                            <div className="px-4 pb-32 space-y-3">
                                <div className="flex items-center justify-between">
                                    <h3 className="text-lg font-bold flex items-center gap-2">
                                        <Moon className="w-5 h-5 icon-sleep" />
                                        {t.quickSleep}
                                    </h3>
                                    <Button variant="ghost" size="icon" onClick={() => setSleepDrawerOpen(false)}>
                                        <X className="w-4 h-4" />
                                    </Button>
                                </div>
                            
                                {true && (
                                    <div className="p-2 rounded-lg bg-primary/10 text-primary text-sm text-center">
                                        {selectedDate.toLocaleDateString(language === 'de' ? 'de-DE' : 'en-US', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                                    </div>
                                )}
                            
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <Label className="text-muted-foreground text-sm">{t.fellAsleep}</Label>
                                        <Input
                                            type="time"
                                            value={sleepForm.sleep_time}
                                            onChange={(e) => setSleepForm({...sleepForm, sleep_time: e.target.value})}
                                            className="bg-muted border-2 border-border h-12 text-lg text-center font-semibold"
                                        />
                                    </div>
                                    <div>
                                        <Label className="text-muted-foreground text-sm">{t.wokeUp}</Label>
                                        <Input
                                            type="time"
                                            value={sleepForm.wake_time}
                                            onChange={(e) => setSleepForm({...sleepForm, wake_time: e.target.value})}
                                            className="bg-muted border-2 border-border h-12 text-lg text-center font-semibold"
                                        />
                                    </div>
                                </div>
                            
                                <div className="text-center text-xl font-bold text-primary">
                                    {calculateSleepDuration()}h
                                </div>
                            
                                <div>
                                    <Label className="text-muted-foreground text-sm">{t.quality}: {sleepForm.quality}/10</Label>
                                    <Slider
                                        value={[sleepForm.quality]}
                                        onValueChange={([v]) => setSleepForm({...sleepForm, quality: v})}
                                        min={1} max={10} step={1}
                                        className="py-3"
                                    />
                                </div>
                            
                                <Button
                                    onClick={handleQuickSleep}
                                    disabled={loading.sleep}
                                    className="w-full h-14 bg-primary text-primary-foreground hover:bg-primary/90 rounded-xl text-base font-bold"
                                    data-testid="quick-sleep-save-btn"
                                >
                                    {loading.sleep ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Check className="w-5 h-5 mr-2" />{language === 'de' ? 'OK — Speichern' : 'OK — Save'}</>}
                                </Button>
                                <div className="h-4" />
                            </div>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>
            
            {/* Meal Drawer */}
            <AnimatePresence>
                {mealDrawerOpen && (
                    <>
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
                            onClick={() => setMealDrawerOpen(false)}
                        />
                        <motion.div
                            initial={{ y: '100%' }}
                            animate={{ y: 0 }}
                            exit={{ y: '100%' }}
                            transition={{ type: 'spring', damping: 25 }}
                            className="fixed bottom-0 left-0 right-0 z-50 bg-card rounded-t-3xl border-t border-border max-h-[85vh] overflow-y-auto"
                        >
                            <div className="flex justify-center py-2">
                                <div className="w-12 h-1.5 bg-muted-foreground/20 rounded-full" />
                            </div>
                            
                            <div className="px-4 pb-32 space-y-3">
                                <div className="flex items-center justify-between">
                                    <h3 className="text-lg font-bold flex items-center gap-2">
                                        <Utensils className="w-5 h-5 icon-fitness" />
                                        {t.quickMeal}
                                    </h3>
                                    <Button variant="ghost" size="icon" onClick={() => setMealDrawerOpen(false)}>
                                        <X className="w-4 h-4" />
                                    </Button>
                                </div>
                            
                                {true && (
                                    <div className="p-2 rounded-lg bg-primary/10 text-primary text-sm text-center">
                                        {selectedDate.toLocaleDateString(language === 'de' ? 'de-DE' : 'en-US', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                                    </div>
                                )}
                            
                                <div className="space-y-3">
                                    <div>
                                        <Label className="text-muted-foreground text-sm">{t.mealName}</Label>
                                        <Input
                                            value={mealForm.name}
                                            onChange={(e) => setMealForm({...mealForm, name: e.target.value})}
                                            placeholder="z.B. Müsli mit Obst"
                                            className="bg-muted border-2 border-border h-11"
                                        />
                                    </div>
                                
                                    <div className="flex gap-2">
                                        {['breakfast', 'lunch', 'dinner', 'snack'].map((type) => (
                                            <Button
                                                key={type}
                                                variant={mealForm.meal_type === type ? 'default' : 'outline'}
                                                size="sm"
                                                onClick={() => setMealForm({...mealForm, meal_type: type})}
                                                className={`flex-1 ${mealForm.meal_type === type ? 'bg-primary' : ''}`}
                                            >
                                                {type === 'breakfast' ? '☀️' : type === 'lunch' ? '🌤️' : type === 'dinner' ? '🌙' : '🍎'}
                                            </Button>
                                        ))}
                                    </div>
                                
                                    <div>
                                        <Label className="text-muted-foreground text-sm">Kalorien: {mealForm.calories} kcal</Label>
                                        <Slider
                                            value={[mealForm.calories]}
                                            onValueChange={([v]) => setMealForm({...mealForm, calories: v})}
                                            min={50} max={1500} step={50}
                                            className="py-3"
                                        />
                                    </div>
                                </div>
                            
                                <Button
                                    onClick={handleQuickMeal}
                                    disabled={loading.meal || !mealForm.name}
                                    className="w-full h-14 bg-primary text-primary-foreground hover:bg-primary/90 rounded-xl text-base font-bold"
                                    data-testid="quick-meal-save-btn"
                                >
                                    {loading.meal ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Check className="w-5 h-5 mr-2" />{language === 'de' ? 'OK — Speichern' : 'OK — Save'}</>}
                                </Button>
                                <div className="h-4" />
                            </div>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>
            
            {/* Workout Drawer */}
            <AnimatePresence>
                {workoutDrawerOpen && (
                    <>
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
                            onClick={() => setWorkoutDrawerOpen(false)}
                        />
                        <motion.div
                            initial={{ y: '100%' }}
                            animate={{ y: 0 }}
                            exit={{ y: '100%' }}
                            transition={{ type: 'spring', damping: 25 }}
                            className="fixed bottom-0 left-0 right-0 z-50 bg-card rounded-t-3xl border-t border-border max-h-[85vh] overflow-y-auto"
                        >
                            <div className="flex justify-center py-2">
                                <div className="w-12 h-1.5 bg-muted-foreground/20 rounded-full" />
                            </div>
                            
                            <div className="px-4 pb-32 space-y-3">
                                <div className="flex items-center justify-between">
                                    <h3 className="text-lg font-bold flex items-center gap-2">
                                        <Dumbbell className="w-5 h-5 icon-health" />
                                        {t.quickWorkout}
                                    </h3>
                                    <Button variant="ghost" size="icon" onClick={() => setWorkoutDrawerOpen(false)}>
                                        <X className="w-4 h-4" />
                                    </Button>
                                </div>
                            
                                {true && (
                                    <div className="p-2 rounded-lg bg-primary/10 text-primary text-sm text-center">
                                        {selectedDate.toLocaleDateString(language === 'de' ? 'de-DE' : 'en-US', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                                    </div>
                                )}
                            
                                <div className="space-y-3">
                                    <div>
                                        <Label className="text-muted-foreground text-sm">{t.workoutName}</Label>
                                        <Input
                                            value={workoutForm.name}
                                            onChange={(e) => setWorkoutForm({...workoutForm, name: e.target.value})}
                                            placeholder="z.B. Joggen, Yoga, Krafttraining"
                                            className="bg-muted border-2 border-border h-11"
                                        />
                                    </div>
                                
                                    <div className="flex gap-2">
                                        {['cardio', 'strength', 'yoga', 'hiit'].map((type) => (
                                            <Button
                                                key={type}
                                                variant={workoutForm.type === type ? 'default' : 'outline'}
                                                size="sm"
                                                onClick={() => setWorkoutForm({...workoutForm, type})}
                                                className={`flex-1 ${workoutForm.type === type ? 'bg-primary' : ''}`}
                                            >
                                                {type === 'cardio' ? '🏃' : type === 'strength' ? '💪' : type === 'yoga' ? '🧘' : '⚡'}
                                            </Button>
                                        ))}
                                    </div>
                                
                                    <div>
                                        <Label className="text-muted-foreground text-sm">{t.duration}: {workoutForm.duration} min</Label>
                                        <div className="flex gap-2 mt-1">
                                            {[15, 30, 45, 60, 90].map((min) => (
                                                <Button
                                                    key={min}
                                                    variant={workoutForm.duration === min ? 'default' : 'outline'}
                                                    size="sm"
                                                    onClick={() => setWorkoutForm({...workoutForm, duration: min})}
                                                    className={`flex-1 ${workoutForm.duration === min ? 'bg-primary' : ''}`}
                                                >
                                                    {min}
                                                </Button>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            
                                <Button
                                    onClick={handleQuickWorkout}
                                    disabled={loading.workout || !workoutForm.name}
                                    className="w-full h-14 bg-primary text-primary-foreground hover:bg-primary/90 rounded-xl text-base font-bold"
                                    data-testid="quick-workout-save-btn"
                                >
                                    {loading.workout ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Check className="w-5 h-5 mr-2" />{language === 'de' ? 'OK — Speichern' : 'OK — Save'}</>}
                                </Button>
                                <div className="h-4" />
                            </div>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>
        </>
    );
}
